import React from "react";

const ProgressShape = ({
  value = 0,
  max = 100,
  amount = 150,
  totalPeople = 2,
}) => {
  const strokeWidth = 10;
  const progress = Math.min(value / max, 1);

  // Calculate path length for polygon
  const calculatePolygonPerimeter = (radius, sides) => {
    const sideLength = 2 * radius * Math.sin(Math.PI / sides);
    return sideLength * sides;
  };

  // Define shapes based on number of people
  const getShapeConfig = () => {
    switch (totalPeople) {
      case 1:
      case 2:
        // Circle
        const radius = 90;
        const circumference = 2 * Math.PI * radius;
        return {
          type: "circle",
          path: `M 120,${120 - radius} A ${radius},${radius} 0 1,1 119.99,${120 - radius}`,
          length: circumference,
          viewBox: "0 0 240 240",
        };

      case 3:
        // Triangle (Equilateral)
        const triangleRadius = 100;
        const trianglePoints = [];
        for (let i = 0; i < 3; i++) {
          const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2;
          const x = 120 + triangleRadius * Math.cos(angle);
          const y = 120 + triangleRadius * Math.sin(angle);
          trianglePoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        trianglePoints.push("Z");
        return {
          type: "triangle",
          path: trianglePoints.join(" "),
          length: calculatePolygonPerimeter(triangleRadius, 3),
          viewBox: "0 0 240 240",
        };

      case 4:
        // Square
        const squareRadius = 105;
        const squarePoints = [];
        for (let i = 0; i < 4; i++) {
          const angle = (Math.PI * 2 * i) / 4 - Math.PI / 4;
          const x = 120 + squareRadius * Math.cos(angle);
          const y = 120 + squareRadius * Math.sin(angle);
          squarePoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        squarePoints.push("Z");
        return {
          type: "square",
          path: squarePoints.join(" "),
          length: calculatePolygonPerimeter(squareRadius, 4),
          viewBox: "0 0 240 240",
        };

      case 5:
        // Pentagon
        const pentagonRadius = 90;
        const pentagonPoints = [];
        for (let i = 0; i < 5; i++) {
          const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
          const x = 120 + pentagonRadius * Math.cos(angle);
          const y = 120 + pentagonRadius * Math.sin(angle);
          pentagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        pentagonPoints.push("Z");
        return {
          type: "pentagon",
          path: pentagonPoints.join(" "),
          length: calculatePolygonPerimeter(pentagonRadius, 5),
          viewBox: "0 0 240 240",
        };

      case 6:
        // Hexagon
        const hexagonRadius = 90;
        const hexagonPoints = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
          const x = 120 + hexagonRadius * Math.cos(angle);
          const y = 120 + hexagonRadius * Math.sin(angle);
          hexagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        hexagonPoints.push("Z");
        return {
          type: "hexagon",
          path: hexagonPoints.join(" "),
          length: calculatePolygonPerimeter(hexagonRadius, 6),
          viewBox: "0 0 240 240",
        };

      case 7:
        // Heptagon
        const heptagonRadius = 90;
        const heptagonPoints = [];
        for (let i = 0; i < 7; i++) {
          const angle = (Math.PI * 2 * i) / 7 - Math.PI / 2;
          const x = 120 + heptagonRadius * Math.cos(angle);
          const y = 120 + heptagonRadius * Math.sin(angle);
          heptagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        heptagonPoints.push("Z");
        return {
          type: "heptagon",
          path: heptagonPoints.join(" "),
          length: calculatePolygonPerimeter(heptagonRadius, 7),
          viewBox: "0 0 240 240",
        };

      case 8:
        // Octagon
        const octagonRadius = 90;
        const octagonPoints = [];
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI * 2 * i) / 8 - Math.PI / 2;
          const x = 120 + octagonRadius * Math.cos(angle);
          const y = 120 + octagonRadius * Math.sin(angle);
          octagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        octagonPoints.push("Z");
        return {
          type: "octagon",
          path: octagonPoints.join(" "),
          length: calculatePolygonPerimeter(octagonRadius, 8),
          viewBox: "0 0 240 240",
        };

      case 9:
        // Nonagon
        const nonagonRadius = 90;
        const nonagonPoints = [];
        for (let i = 0; i < 9; i++) {
          const angle = (Math.PI * 2 * i) / 9 - Math.PI / 2;
          const x = 120 + nonagonRadius * Math.cos(angle);
          const y = 120 + nonagonRadius * Math.sin(angle);
          nonagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        nonagonPoints.push("Z");
        return {
          type: "nonagon",
          path: nonagonPoints.join(" "),
          length: calculatePolygonPerimeter(nonagonRadius, 9),
          viewBox: "0 0 240 240",
        };

      case 10:
        // Decagon
        const decagonRadius = 90;
        const decagonPoints = [];
        for (let i = 0; i < 10; i++) {
          const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
          const x = 120 + decagonRadius * Math.cos(angle);
          const y = 120 + decagonRadius * Math.sin(angle);
          decagonPoints.push(`${i === 0 ? "M" : "L"} ${x},${y}`);
        }
        decagonPoints.push("Z");
        return {
          type: "decagon",
          path: decagonPoints.join(" "),
          length: calculatePolygonPerimeter(decagonRadius, 10),
          viewBox: "0 0 240 240",
        };

      default:
        // For 11+ people, use circle
        const defaultRadius = 90;
        const defaultCircumference = 2 * Math.PI * defaultRadius;
        return {
          type: "circle",
          path: `M 120,${120 - defaultRadius} A ${defaultRadius},${defaultRadius} 0 1,1 119.99,${120 - defaultRadius}`,
          length: defaultCircumference,
          viewBox: "0 0 240 240",
        };
    }
  };

  const shapeConfig = getShapeConfig();
  const strokeDashoffset = shapeConfig.length - progress * shapeConfig.length;

  // Get shape label
  const getShapeLabel = () => {
    const labels = {
      circle: "Circle",
      triangle: "Triangle",
      square: "Square",
      pentagon: "Pentagon",
      hexagon: "Hexagon",
      heptagon: "Heptagon",
      octagon: "Octagon",
      nonagon: "Nonagon",
      decagon: "Decagon",
    };
    return labels[shapeConfig.type] || "Shape";
  };

  return (
    <div className="flex flex-col items-center justify-center relative my-2">
      {/* Shape Label */}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
          {getShapeLabel()} - {totalPeople}{" "}
          {totalPeople === 1 ? "Person" : "People"}
        </span>
      </div>
      <div className="relative">
        <svg viewBox={shapeConfig.viewBox} className="w-[300px] h-[300px]">
          {/* Background shape */}
          <path
            d={shapeConfig.path}
            stroke="#E5E5E5"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinejoin="round"
          />

          {/* Progress shape */}
          <path
            d={shapeConfig.path}
            stroke="#3b82f6"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={shapeConfig.length}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500 ease-in-out"
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-[28px] font-bold text-blue-500 leading-none">
            ${amount.toFixed(2)}
          </p>
          <p className="text-xs text-slate-400 font-normal">
            Total Bill Amount
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressShape;
